import { cn, formatCurrency, formatNumber, formatPercentage } from '@/lib/utils'
import { LucideIcon, TrendingUp, TrendingDown, Minus } from 'lucide-react'

interface MetricCardProps {
  title: string
  value: string | number
  subtitle?: string
  trend?: 'up' | 'down' | 'neutral'
  trendValue?: string
  icon?: LucideIcon
  format?: 'currency' | 'number' | 'percentage' | 'text'
  color?: 'default' | 'success' | 'warning' | 'danger' | 'info'
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

const colorVariants = {
  default: 'bg-white border-gray-200',
  success: 'bg-risk-excellent/10 border-risk-excellent/30',
  warning: 'bg-risk-fair/10 border-risk-fair/30',
  danger: 'bg-risk-critical/10 border-risk-critical/30',
  info: 'bg-dashboard-teal/10 border-dashboard-teal/30',
}

const iconColorVariants = {
  default: 'text-gray-500',
  success: 'text-risk-excellent',
  warning: 'text-risk-fair',
  danger: 'text-risk-critical',
  info: 'text-dashboard-teal',
}

const sizeVariants = {
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
}

const valueSizeVariants = {
  sm: 'text-xl',
  md: 'text-2xl',
  lg: 'text-3xl',
}

export function MetricCard({
  title,
  value,
  subtitle,
  trend,
  trendValue,
  icon: Icon,
  format = 'text',
  color = 'default',
  size = 'md',
  className,
}: MetricCardProps) {
  const formattedValue = (() => {
    if (typeof value === 'string') return value
    switch (format) {
      case 'currency':
        return formatCurrency(value)
      case 'number':
        return formatNumber(value)
      case 'percentage':
        return formatPercentage(value)
      default:
        return String(value)
    }
  })()

  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus

  return (
    <div
      className={cn(
        'rounded-lg border snippet-card',
        colorVariants[color],
        sizeVariants[size],
        className
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {title}
          </p>
          <p className={cn('font-bold mt-1', valueSizeVariants[size])}>
            {formattedValue}
          </p>
          {subtitle && (
            <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
          )}
          {trend && trendValue && (
            <div className={cn(
              'flex items-center gap-1 mt-2 text-xs',
              trend === 'up' ? 'text-risk-excellent' : trend === 'down' ? 'text-risk-critical' : 'text-gray-500'
            )}>
              <TrendIcon className="h-3 w-3" />
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        {Icon && (
          <div className={cn('p-2 rounded-lg bg-white/50', iconColorVariants[color])}>
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </div>
  )
}
